import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoaderData } from '@remix-run/react';
import type { EmptyChildren, PostTheme } from '@shared/dream-components';
import { PostContentElement } from '@shared/dream-components';
import type { JSONContent } from '@tiptap/core';

import type { StaticPageLoaderData } from '~/services/static_page';
import rewriteSrc from '~/utils/rewriteSrc';

import { ChildDecoratorComponent } from './PostContent/ChildDecoratorComponent';

interface Props {
  content: JSONContent;
}

/**
 * Creates a minimal PostTheme from webTheme properties for custom pages.
 * Custom pages don't have a post theme, so we derive styles from the web theme.
 */
const createPostThemeFromWebTheme = (webTheme: StaticPageLoaderData['site']['publication']['web_theme']): PostTheme => {
  return {
    // Button styles - map from web theme primary colors
    button_background_color: webTheme.primary_color,
    button_text_color: webTheme.text_on_primary_color,
    button_font_family: webTheme.button_font,
    button_border_radius: webTheme.border_radius,
    // Paragraph and text styles
    paragraph_font_family: webTheme.body_font,
    paragraph_color: webTheme.text_on_background_color,
    // Heading styles
    h1_font_family: webTheme.header_font,
    h1_font_color: webTheme.text_on_background_color,
    h2_font_family: webTheme.header_font,
    h2_font_color: webTheme.text_on_background_color,
    h3_font_family: webTheme.header_font,
    h3_font_color: webTheme.text_on_background_color,
    h4_font_family: webTheme.header_font,
    h4_font_color: webTheme.text_on_background_color,
    h5_font_family: webTheme.header_font,
    h5_font_color: webTheme.text_on_background_color,
    h6_font_family: webTheme.header_font,
    h6_font_color: webTheme.text_on_background_color,
    // Link styles
    inline_link_color: webTheme.primary_color,
    // List styles
    list_font_family: webTheme.body_font,
    list_font_color: webTheme.text_on_background_color,
    // Background
    background_color: webTheme.background_color,
  } as PostTheme;
};

/**
 * Simplified component for rendering post editor content on custom pages.
 * Unlike PostContent, this doesn't require post-specific context (paywall, merge tags, etc.)
 */
const PostEditorCustomPageContent = ({ content }: Props) => {
  const { site, page } = useLoaderData<StaticPageLoaderData>();
  const { t } = useTranslation();

  // Use actual post_theme from backend for legacy pages, fallback to synthetic theme
  const postTheme = useMemo(
    () => page.post_theme ?? createPostThemeFromWebTheme(site.publication.web_theme),
    [page.post_theme, site.publication.web_theme],
  );

  // Transform the content structure to match what PostContentElement expects
  const element = {
    type: 'postContent' as const,
    attrs: {
      id: 'page-post-content',
      data: content,
    },
    content: [{ id: 'empty', text: '' }] as EmptyChildren,
  };

  return (
    <PostContentElement
      id="content-blocks"
      webTheme={site.publication.web_theme}
      postTheme={postTheme}
      element={element}
      isLive
      translations={{
        'login.already_a_subscriber': t('login.already_a_subscriber'),
        'shared.cta.sign_in': t('shared.cta.sign_in'),
        'login.not_now': t('login.not_now'),
        'shared.actions.submitting': t('shared.actions.submitting'),
        'subscribe.email_placeholder': t('subscribe.email_placeholder'),
        'shared.cta.subscribe': t('shared.cta.subscribe'),
        'shared.cta.login': t('shared.cta.login'),
        'shared.cta.or': t('shared.cta.or'),
        'shared.cta.to_participate': t('shared.cta.to_participate'),
        'shared.poll.subscribe_text': t('shared.poll.subscribe_text'),
        'shared.image.missing_image': t('shared.image.missing_image'),
        'shared.cta.gdpr_signup_consent': t('shared.cta.gdpr_signup_consent'),
        'footer.terms_of_use': t('footer.terms_of_use'),
        'footer.privacy_policy': t('footer.privacy_policy'),
        'shared.cta.and': t('shared.cta.and'),
        'shared.cta.sign_up': t('shared.cta.sign_up'),
        'meta.titles.tos': t('meta.titles.tos'),
      }}
      rewriteImageSrc={rewriteSrc}
      loginPath="/login"
      subscribePath="/subscribe"
      homePath="/"
      upgradePath="/upgrade"
      showSubscriberBreak={false}
      showPaywallBreak={false}
      ChildDecoratorComponent={ChildDecoratorComponent}
      isIndexingDisabled={site?.viewable_site_version?.metadata?.noindex_enabled === true}
      isSubscriberAuthenticated={false}
    >
      {null}
    </PostContentElement>
  );
};

export { PostEditorCustomPageContent };
